import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Clock, Tag } from "lucide-react";
import SEO from "@/components/SEO";
import { FadeUp, CTABand } from "@/components/Reveal";
import { ARTICLES } from "@/data/articles";

export default function Artikel() {
  const { slug } = useParams();
  const article = ARTICLES.find((a) => a.slug === slug);

  if (!article) {
    return (
      <section className="mx-auto max-w-2xl px-5 py-24 text-center md:py-32" data-testid="artikel-not-found">
        <h1 className="font-display text-3xl font-semibold tracking-tight text-ink">Artikel niet gevonden</h1>
        <p className="mt-4 text-muted-foreground">Dit artikel bestaat niet of is verplaatst.</p>
        <Link to="/kennisbank" data-testid="artikel-back" className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-primary hover:text-energy-light">
          <ArrowLeft size={15} />
          Terug naar de kennisbank
        </Link>
      </section>
    );
  }

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.description,
    author: { "@type": "Organization", name: "Directelabels", url: "https://directelabels.nl" },
    publisher: { "@type": "Organization", name: "Directelabels", url: "https://directelabels.nl" },
    mainEntityOfPage: `https://directelabels.nl/kennisbank/${article.slug}`,
  };

  return (
    <>
      <SEO
        title={`${article.title} | Directelabels`}
        description={article.description}
        path={`/kennisbank/${article.slug}`}
        jsonLd={jsonLd}
      />

      <article data-testid="artikel-page" className="mx-auto max-w-3xl px-5 py-16 md:py-24">
        <FadeUp>
          <Link to="/kennisbank" data-testid="artikel-terug" className="inline-flex items-center gap-2 text-sm font-bold text-primary transition-colors duration-200 hover:text-energy-light">
            <ArrowLeft size={15} />
            Terug naar de kennisbank
          </Link>
          <div className="mt-8 flex flex-wrap items-center gap-3 text-xs font-bold text-muted-foreground">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-energy-pale px-3 py-1 text-energy-deep">
              <Tag size={11} />
              {article.category}
            </span>
            <span>{article.date}</span>
            <span className="inline-flex items-center gap-1.5">
              <Clock size={12} />
              {article.readTime}
            </span>
          </div>
          <h1 data-testid="artikel-title" className="mt-5 font-display text-3xl font-semibold leading-tight tracking-tighter text-ink md:text-5xl">
            {article.title}
          </h1>
          <p className="mt-6 border-l-2 border-primary pl-5 text-base leading-relaxed text-ink/80 md:text-lg">
            {article.intro}
          </p>
        </FadeUp>

        <div className="mt-12 space-y-12">
          {article.sections.map((s, i) => (
            <FadeUp key={s.heading} delay={0.05}>
              <section data-testid={`artikel-sectie-${i}`}>
                <h2 className="font-display text-2xl font-medium tracking-tight text-ink md:text-3xl">{s.heading}</h2>
                {s.paragraphs.map((p, j) => (
                  <p key={j} className="mt-4 text-base leading-relaxed text-muted-foreground">
                    {p}
                  </p>
                ))}
              </section>
            </FadeUp>
          ))}
        </div>

        <FadeUp>
          <div className="mt-14 rounded-3xl border border-primary/20 bg-energy-pale/60 p-8 md:p-10">
            <h2 className="font-display text-xl font-medium tracking-tight text-ink md:text-2xl">
              Direct uw energielabel regelen?
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground md:text-base">
              Directelabels voert professionele EP-W woningopnames uit in Rotterdam en omgeving. Vaste
              introductietarieven vanaf €139 inclusief btw en registratie in EP-online.
            </p>
            <div className="mt-6 flex flex-wrap gap-4">
              <Link
                to="/aanvragen"
                data-testid="artikel-cta-aanvragen"
                className="inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-bold text-white transition-colors duration-200 hover:bg-energy-light"
              >
                Energielabel aanvragen
              </Link>
              <Link
                to="/tarieven"
                data-testid="artikel-cta-tarieven"
                className="inline-flex items-center gap-2 rounded-full border border-ink/15 px-7 py-3.5 text-sm font-bold text-ink transition-colors duration-200 hover:border-primary hover:text-primary"
              >
                Bekijk tarieven
              </Link>
            </div>
          </div>
        </FadeUp>
      </article>

      <CTABand />
    </>
  );
}
